import {defineStore} from 'pinia'
import {ref, computed, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import type {OrderPayload, RegisteredOrderPayload, OrderFee} from '~/types/order'
import type {PaymentMethodOption, TransportInformOption} from '~/types/delivery'
import type {IdentityView} from '~/types/identity'
import {
    clientInfoStatus,
    type CompanyView,
    type LinkedClientStatus,
} from '~/utils/client-invoice'
import {
    companyFromIdentityClient,
    identityWantsInvoice,
    isLinkedIdentity,
    ordersAsPendingRequest,
    pendingRequest,
} from '~/utils/identity'
import {
    invoiceIdsFromNoms,
    orderInvoiceTypeId,
    type InvoiceTypeIds,
    type OrderNomsResponse,
} from '~/utils/order-invoice'
import {FeeId, PaymentMethods, TransportInforms} from '~/constants/delivery'
import {ORGANIZATION} from '~/constants/organization'
import {getCityDeliveryRules} from '~/constants/cityDelivery'
import {isGallonTypeCat, isStackItem, is19lPackaging} from '~/utils/products'
import {formatDayLabelInSentence, formatOrderSummaryDate, getDeliveryDayOptions} from '~/utils/delivery'
import {floorsToClimbCount} from '~/utils/floor'
import {displayAccountPhone} from '~/utils/phone'
import {useCartStore} from '~/stores/cart'

export type OrderElevatorChoice = 'yes' | 'no' | 'none'

export const useOrderStore = defineStore('order', () => {
    const {t, locale} = useI18n()
    const cartStore = useCartStore()

    // ── Address ──
    const address = ref('')
    const coordinates = ref<{ lat: number; lng: number } | null>(null)
    const city = ref('')
    /** City of the saved address chosen at checkout. Not persisted: it must not reach the guest form. */
    const savedAddressCity = ref<string | null>(null)
    const neighborhood = ref('')
    const street = ref('')
    const block = ref('')
    const entrance = ref('')
    const floor = ref('')
    const apt = ref('')
    const elevatorChoice = ref<OrderElevatorChoice | null>(null)
    /** Derived from elevatorChoice so persist restore cannot leave them out of sync. */
    const hasElevator = computed<boolean | null>(() => {
        if (elevatorChoice.value === 'no') return false
        if (elevatorChoice.value === 'yes' || elevatorChoice.value === 'none') return true
        return null
    })
    const bringToTheDoor = ref<boolean | null>(null)
    const floor1IsGroundLevel = ref(false)
    const deliveryComment = ref('')
    const additionalInfo = ref('')

    // ── Contact person ──
    const firstName = ref('')
    const lastName = ref('')
    const email = ref('')
    const phone = ref('')

    // ── Delivery ──
    const selectedDeliveryDay = ref('')
    const tryEarlierIfPossible = ref(false)
    const callBeforeArrival = ref(false)

    function localizedPreCallTiming(
        key: keyof typeof TransportInforms = 30,
    ): TransportInformOption {
        const entry = TransportInforms[key]
        return { label: t(entry.label), value: entry.value }
    }

    function localizedPaymentMethod(
        method: PaymentMethodOption = PaymentMethods.cash,
    ): PaymentMethodOption {
        return { label: t(method.label), value: method.value }
    }

    const preCallTiming = ref<TransportInformOption>(localizedPreCallTiming(30))

    // An unchecked "call before arrival" is its own nomenclature row
    // ("Без предварително обаждане", id 5), not the last chosen timing.
    const transportInformTypeId = computed(() =>
        callBeforeArrival.value
            ? preCallTiming.value.value
            : TransportInforms[0].value,
    )

    watch(preCallTiming, (val) => {
        if (!val) preCallTiming.value = localizedPreCallTiming(30)
    })

    watch(callBeforeArrival, (enabled) => {
        if (enabled) preCallTiming.value = localizedPreCallTiming(30)
    })

    // ── Options ──
    const acceptedTerms = ref(false)
    const contactlessDelivery = ref(false)

    // ── Payment ──
    const paymentMethod = ref<PaymentMethodOption>(localizedPaymentMethod())

    // ── Invoice ──
    const wantInvoice = ref(false)
    const eikNumber = ref('')
    const eikLoading = ref(false)
    const eikError = ref('')

    function eikErrorMessage(code: string) {
        return t(`clientInvoice.errors.eik.${code}`, {
            phone: ORGANIZATION.telephone,
            email: ORGANIZATION.email,
        })
    }

    // ── Account with a client: the active client (private person / company) decides the invoice ──
    const api = useApi()
    const identity = ref<IdentityView | null>(null)
    /** loading / error: the invoice choice is unknown until the identity is loaded. */
    const identityStatus = ref<LinkedClientStatus>('none')
    /** With a pending company request: the order waits for that company. */
    const asRequest = ref(false)
    const linked = computed(() => isLinkedIdentity(identity.value))
    const companyLookup = useCompanyLookup(() => !linked.value)

    watch(() => companyLookup.loading.value, (loading) => {
        eikLoading.value = loading
    }, {flush: 'sync'})
    watch(() => companyLookup.errorCode.value, (code) => {
        eikError.value = code ? eikErrorMessage(code) : ''
    }, {flush: 'sync'})

    watch(wantInvoice, (val) => {
        if (val) return
        eikNumber.value = ''
        companyLookup.reset()
        eikError.value = ''
    })

    /** order-v1 keeps only the guest's invoice; a linked account's choice never reaches the browser copy. */
    const guestInvoice = ref<{ want: boolean, eik: string }>({want: false, eik: ''})

    watch([wantInvoice, eikNumber], ([want, eik]) => {
        if (identityStatus.value === 'none' && !linked.value) guestInvoice.value = {want, eik}
    })

    /** For a guest or an account without a client, after setIdentity(null). */
    function restoreGuestInvoice() {
        if (identityStatus.value !== 'none' || linked.value) return
        wantInvoice.value = guestInvoice.value.want
        eikNumber.value = guestInvoice.value.want ? guestInvoice.value.eik : ''
    }

    /** null or an account without a client: behave like a guest. */
    function setIdentity(view: IdentityView | null) {
        companyLookup.reset()
        eikError.value = ''
        identity.value = view
        identityStatus.value = isLinkedIdentity(view) ? 'linked' : 'none'
        asRequest.value = false
    }

    let identityRequest: Promise<void> | null = null

    function loadIdentity(fetchIdentity: () => Promise<IdentityView>): Promise<void> {
        identity.value = null
        identityStatus.value = 'loading'
        wantInvoice.value = false
        eikNumber.value = ''
        eikError.value = ''
        companyLookup.reset()
        const request = fetchIdentity()
            .then(view => setIdentity(view))
            .catch((error) => {
                setIdentity(null)
                identityStatus.value = clientInfoStatus(error)
            })
            .finally(() => {
                if (identityRequest === request) identityRequest = null
            })
        identityRequest = request
        return request
    }

    /** Resolves once a running identity load has finished. */
    function waitIdentity(): Promise<void> {
        return identityRequest ?? Promise.resolve()
    }

    /** A linked account's active company, or the guest's looked-up company. */
    const invoiceCompany = computed<CompanyView | null>(() => {
        if (linked.value) return companyFromIdentityClient(identity.value?.active)
        return companyLookup.company.value
    })

    /** Name for the summary: the active company, or the pending request's company. */
    const invoiceCompanyName = computed(() => {
        if (!linked.value) return invoiceCompany.value?.name?.trim() ?? ''
        if (identity.value?.active?.type === 'company') return identity.value.active.name.trim()
        const request = pendingRequest(identity.value)
        return asRequest.value && request ? (request.companyName || request.eik) : ''
    })

    // ── Invoice method ids (guest and registered without a linked client) ──
    const orderNomsInvoiceIds = ref<InvoiceTypeIds | null>(null)
    let orderNomsRequest: Promise<void> | null = null

    /** Loaded once; a failed load is retried on the next call. */
    function loadOrderNoms(): Promise<void> {
        if (orderNomsInvoiceIds.value) return Promise.resolve()
        orderNomsRequest ??= api<OrderNomsResponse>('/Public/OrderNoms')
            .then((data) => {
                orderNomsInvoiceIds.value = invoiceIdsFromNoms(data)
            })
            .catch(() => {})
            .finally(() => {
                orderNomsRequest = null
            })
        return orderNomsRequest
    }

    const orderHasInvoice = computed(() => linked.value
        ? identityWantsInvoice(identity.value, asRequest.value)
        : wantInvoice.value)
    /** A linked account's order takes it from its client on the server, so it stays null. */
    const invoiceTypeId = computed(() => linked.value
        ? null
        : orderInvoiceTypeId(wantInvoice.value, orderNomsInvoiceIds.value))
    const invoiceTypeReady = computed(() => linked.value || invoiceTypeId.value != null)
    const orderAsRequest = computed(() =>
        linked.value && ordersAsPendingRequest(identity.value, asRequest.value))

    /** Debounced lookup through the API (guest and account without a client). */
    function lookupEik(val: string, options?: { immediate?: boolean }) {
        eikNumber.value = val
        eikError.value = ''
        companyLookup.lookup(val, options)
    }

    // ── Derived computeds (needed for payload) ──
    const cityRules = computed(() => getCityDeliveryRules(savedAddressCity.value ?? city.value))
    const deliverToSofia = computed(() => !cityRules.value.courierRequired)
    const isCourierRequired = computed(() => cityRules.value.courierRequired)

    const deliveryDayOptions = computed(() => getDeliveryDayOptions(t, locale.value, {
        skipEarliest: cityRules.value.skipEarliestDeliveryDay,
        count: cityRules.value.deliveryDayCount,
    }))

    function defaultSelectedDeliveryDate(): string {
        const options = deliveryDayOptions.value
        return options.find(option => !option.note)?.date ?? options[0]?.date ?? ''
    }

    function applyCityProfileSideEffects() {
        const rules = cityRules.value
        if (!rules.showContactless) contactlessDelivery.value = false
        if (!rules.showCallBeforeArrival) callBeforeArrival.value = false
        if (!rules.showElevator) {
            elevatorChoice.value = null
            floor1IsGroundLevel.value = false
        }
        if (!rules.showBringToDoor) bringToTheDoor.value = null
        if (!rules.showAddressComment) additionalInfo.value = ''
        if (rules.skipEarliestDeliveryDay) tryEarlierIfPossible.value = false

        const dates = deliveryDayOptions.value
        if (!dates.some(option => option.date === selectedDeliveryDay.value)) {
            selectedDeliveryDay.value = defaultSelectedDeliveryDate()
            tryEarlierIfPossible.value = false
        }

        revalidatePaymentMethod()
    }

    function setDefaultPaymentMethod() {
        const method = cityRules.value.showCashOnDelivery ? PaymentMethods.cash : PaymentMethods.cod
        paymentMethod.value = localizedPaymentMethod(method)
    }

    function isPaymentMethodAvailable(value: number): boolean {
        return cityRules.value.showCashOnDelivery
            ? value !== PaymentMethods.cod.value
            : value !== PaymentMethods.cash.value
    }

    function syncLocalizedOptions() {
        const paymentEntry = Object.values(PaymentMethods).find(
            (m) => m.value === paymentMethod.value.value,
        )
        if (paymentEntry) {
            paymentMethod.value = {
                label: t(paymentEntry.label),
                value: paymentEntry.value,
            }
        }

        const preCallValue = preCallTiming.value?.value
        const preCallEntry = Object.values(TransportInforms).find(
            (o) => o.value === preCallValue,
        )
        if (preCallEntry) {
            preCallTiming.value = {
                label: t(preCallEntry.label),
                value: preCallEntry.value,
            }
        }
    }

    function revalidatePaymentMethod() {
        if (!isPaymentMethodAvailable(paymentMethod.value.value)) {
            setDefaultPaymentMethod()
        }
    }

    watch(isCourierRequired, () => {
        revalidatePaymentMethod()
    })

    watch(deliveryDayOptions, (options) => {
        if (!selectedDeliveryDay.value) return
        if (options.some(option => option.date === selectedDeliveryDay.value)) return
        selectedDeliveryDay.value = defaultSelectedDeliveryDate()
        tryEarlierIfPossible.value = false
    })

    watch(locale, () => {
        syncLocalizedOptions()
    })

    function setElevatorChoice(choice: OrderElevatorChoice) {
        elevatorChoice.value = choice
        floor1IsGroundLevel.value = false
    }

    function migrateElevatorPersist() {
        if (elevatorChoice.value !== null) return
        if (!import.meta.client) return
        try {
            const raw = localStorage.getItem('order-v1')
            if (!raw) return
            const parsed = JSON.parse(raw) as { elevatorChoice?: OrderElevatorChoice; hasElevator?: boolean | null }
            if (parsed.elevatorChoice) {
                elevatorChoice.value = parsed.elevatorChoice
            } else if (parsed.hasElevator === false) {
                elevatorChoice.value = 'no'
            } else if (parsed.hasElevator === true) {
                elevatorChoice.value = 'yes'
            }
        } catch {
            // ignore corrupt localStorage
        }
    }

    function handlePersistRestore() {
        migrateElevatorPersist()
        if (elevatorChoice.value === 'none') elevatorChoice.value = null
        bringToTheDoor.value = null
        floor1IsGroundLevel.value = false
        syncLocalizedOptions()
        applyCityProfileSideEffects()
    }

    /** On logout: the next person on this browser must not get the address, contact or invoice. */
    function clearPersonalData() {
        address.value = ''
        coordinates.value = null
        city.value = ''
        savedAddressCity.value = null
        neighborhood.value = ''
        street.value = ''
        block.value = ''
        entrance.value = ''
        floor.value = ''
        apt.value = ''
        elevatorChoice.value = null
        bringToTheDoor.value = null
        floor1IsGroundLevel.value = false
        deliveryComment.value = ''
        additionalInfo.value = ''
        firstName.value = ''
        lastName.value = ''
        email.value = ''
        phone.value = ''
        callBeforeArrival.value = false
        preCallTiming.value = localizedPreCallTiming(30)
        contactlessDelivery.value = false
        paymentMethod.value = localizedPaymentMethod()
        setLinkedClient(null)
        wantInvoice.value = false
        eikNumber.value = ''
        eikError.value = ''
        guestInvoice.value = {want: false, eik: ''}
    }

    /** On login: the contact comes from the account, not from a guest who filled the form before. */
    function applyAccountContact(user: { firstName: string, lastName: string, phone: string }) {
        if (user.firstName.trim()) firstName.value = user.firstName.trim()
        if (user.lastName.trim()) lastName.value = user.lastName.trim()
        const accountPhone = displayAccountPhone(user.phone)
        if (accountPhone) phone.value = accountPhone
    }

    const computedFees = computed<OrderFee[]>(() => {
        const fees: OrderFee[] = []
        const climb = floorsToClimbCount(floor.value, hasElevator.value, {
            bringToTheDoor: bringToTheDoor.value,
            floor1IsGroundLevel: floor1IsGroundLevel.value,
        })

        if (deliverToSofia.value && climb > 0) {
            const gallonCount = cartStore.items.reduce(
                (sum, item) =>
                    isGallonTypeCat(item.typeCatName || '')
                        ? sum + cartStore.getDisplayQuantity(item.id)
                        : sum,
                0,
            )
            const stackCount = cartStore.items.reduce(
                (sum, item) =>
                    isStackItem(item.typeCatName || '')
                        ? sum + cartStore.getDisplayQuantity(item.id)
                        : sum,
                0,
            )
            if (gallonCount > 0)
                fees.push({feeId: FeeId.StaircaseGallon, quantity: gallonCount * climb})
            if (stackCount > 0)
                fees.push({feeId: FeeId.StaircaseStack, quantity: stackCount * 0.5 * climb})
        }


        if (!deliverToSofia.value) {
            fees.push({feeId: FeeId.Courier, quantity: 1})
        } else if (!cartStore.deliveryInfo.freeDelivery) {
            fees.push({feeId: FeeId.BelowMinOrder, quantity: 1})
        }

        return fees
    })


    // ── Order payload builder ──
    function buildOrderPayload(): OrderPayload
    function buildOrderPayload(registered: { customerId: number; deliveryAddressId: number, phone: string, email: string }): RegisteredOrderPayload
    function buildOrderPayload(registered?: { customerId: number; deliveryAddressId: number, phone: string, email: string }): OrderPayload | RegisteredOrderPayload {
        if (registered) {
            return buildRegisteredOrderPayload(registered.customerId, registered.deliveryAddressId, registered.phone, registered.email)
        }
        return buildGuestOrderPayload()
    }

    function resolveDeliveryDateForApi(): string {
        if (tryEarlierIfPossible.value) {
            const earliest = deliveryDayOptions.value.find(option => option.note)
            if (earliest?.date) return earliest.date
        }
        return selectedDeliveryDay.value
    }

    function deliveryDateTextForCustomer(): string {
        const selectedOpt = deliveryDayOptions.value.find(o => o.date === selectedDeliveryDay.value)
        const selected = selectedOpt ? formatOrderSummaryDate(selectedOpt.date, locale.value) : ''
        if (!tryEarlierIfPossible.value) return selected
        const earliest = deliveryDayOptions.value.find(option => option.note)
            ?? deliveryDayOptions.value[0]
        if (!earliest) return selected
        const earlier = t('order.earlierIfPossibleBrief', {
            when: formatOrderSummaryDate(earliest.date, locale.value),
        })
        return selected ? `${selected} (${earlier})` : earlier
    }

    function appendEarlierIfPossibleNote(notesParts: string[]) {
        if (!tryEarlierIfPossible.value) return
        const earliest = deliveryDayOptions.value.find(option => option.note)
        if (!earliest) return
        notesParts.push(t('order.earlierIfPossibleOrderNote', {
            dayLabel: formatDayLabelInSentence(earliest.label, locale.value, t),
            date: selectedDeliveryDay.value,
        }))
    }

    type InvoicePayloadFields = Pick<OrderPayload,
        'hasInvoice' | 'companyInvoice' | 'invoiceCompIdNum' | 'invoiceName' | 'invoiceVatNumber' | 'invoiceMol' | 'invoiceAddress'>

    function emptyInvoiceAddress(contactPhone: string, contactEmail: string) {
        return {
            city: '',
            district: '',
            street: '',
            streetNumber: '',
            block: '',
            entrance: '',
            floor: '',
            apartment: '',
            additionalInfo: '',
            postcode: '',
            country: 'Bulgaria',
            lat: null,
            lon: null,
            phone: contactPhone,
            email: contactEmail,
            floorsToClimb: '0',
            deliveryStartTime: null,
            deliveryEndTime: null,
        }
    }

    /**
     * For a linked account the server recomputes these from the active client; NeoSoft itself only takes
     * invoice_type and the client. The register address is one line (district, street, number, block…),
     * so it goes into `street`.
     */
    function invoicePayloadFields(contactPhone: string, contactEmail: string): InvoicePayloadFields {
        const hasInvoice = orderHasInvoice.value
        if (!hasInvoice) return {hasInvoice, companyInvoice: false}

        const company = invoiceCompany.value
        return {
            hasInvoice,
            companyInvoice: !!company,
            invoiceCompIdNum: company?.uic ?? null,
            invoiceName: company?.name ?? null,
            invoiceVatNumber: company?.vat ?? null,
            invoiceMol: null,
            invoiceAddress: company
                ? {
                    ...emptyInvoiceAddress(contactPhone, contactEmail),
                    city: company.city ?? '',
                    street: company.address ?? '',
                }
                : null,
        }
    }

    function buildRegisteredOrderPayload(customerId: number, deliveryAddressId: number, phone: string, email: string): RegisteredOrderPayload {
        const deliveryDate = resolveDeliveryDateForApi()
        const deliveryStartTime = deliveryDate ? `${deliveryDate}T08:30:00+00:00` : null
        const deliveryEndTime = deliveryDate ? `${deliveryDate}T17:30:00+00:00` : null

        const items = cartStore.items.map((item) => {
            const packagingId = item.packaging?.[0]?.id ?? null
            const quantityPack = is19lPackaging(item.packaging)
                ? cartStore.getDisplayQuantity(item.id)
                : 0
            return {
                productId: item.id,
                quantity: item.quantity,
                packagingId,
                quantityPack,
            }
        })

        let remaining = cartStore.totalBottlesToReturn
        for (const item of items) {
            if (remaining <= 0) break
            if (item.quantityPack > 0) {
                const reduction = Math.min(item.quantityPack, remaining)
                item.quantityPack -= reduction
                remaining -= reduction
            }
        }

        const fees: OrderFee[] = computedFees.value

        const notesParts: string[] = []
        if (cartStore.bottlesFromOthers) {
            notesParts.push(
                `Клиентът връща ${cartStore.bottlesFromOthers} празни галона от друг доставчик`,
            )
        }
        if (deliveryComment.value) notesParts.push(deliveryComment.value)
        if (cityRules.value.showContactless && contactlessDelivery.value) {
            notesParts.push(t('order.contactlessDelivery'))
        }
        appendEarlierIfPossibleNote(notesParts)

        const payload: RegisteredOrderPayload = {
            email,
            phone,
            shippingAddress: {
                id: deliveryAddressId,
                hasElevator: hasElevator.value,
                bringToTheDoor: bringToTheDoor.value,
                floorsToClimb: String(floorsToClimbCount(floor.value, hasElevator.value, {
                    bringToTheDoor: bringToTheDoor.value,
                    floor1IsGroundLevel: floor1IsGroundLevel.value,
                })),
            },
            ...invoicePayloadFields('', ''),
            ...(orderAsRequest.value ? {asRequest: true} : {}),
            deliveryDateText: deliveryDateTextForCustomer() || undefined,
            order: {
                payTypeId: paymentMethod.value.value,
                invoiceTypeId: invoiceTypeId.value,
                supplyPartyId: isCourierRequired.value ? 2 : 1,
                transportModeId: isCourierRequired.value ? 7 : 10,
                transportInformTypeId: transportInformTypeId.value,
                note: deliveryComment.value,
                deliveryStartTime,
                deliveryEndTime,
                items,
                fees,
            },
            notes: notesParts.join(' | '),
            createdByAdminId: null,
            ...(paymentMethod.value.value === PaymentMethods.card.value
                ? { awaitCardPayment: true }
                : {}),
        }

        return payload
    }

    function buildGuestOrderPayload(): OrderPayload {
        // TODO: extract delivery date generation or leave it to the backend
        const deliveryDate = resolveDeliveryDateForApi()
        const deliveryStartTime = deliveryDate ? `${deliveryDate}T08:30:00+00:00` : null
        const deliveryEndTime = deliveryDate ? `${deliveryDate}T17:30:00+00:00` : null

        // Parse street into street name and number
        const streetParts = street.value.trim().match(/^(.+?)\s+(\d+.*)$/)
        const streetName = streetParts ? streetParts[1]! : street.value
        const streetNum = streetParts ? streetParts[2]! : ''

        const floorsToClimb = String(
            floorsToClimbCount(floor.value, hasElevator.value, {
                bringToTheDoor: bringToTheDoor.value,
                floor1IsGroundLevel: floor1IsGroundLevel.value,
            }),
        )

        const shippingAddress = {
            city: city.value,
            district: neighborhood.value,
            street: streetName,
            streetNumber: streetNum,
            block: block.value,
            entrance: entrance.value,
            floor: floor.value.trim(),
            apartment: apt.value,
            additionalInfo: cityRules.value.showAddressComment ? additionalInfo.value : '',
            postcode: '',
            country: 'Bulgaria',
            lat: coordinates.value?.lat ?? null,
            lon: coordinates.value?.lng ?? null,
            phone: phone.value,
            email: email.value,
            floorsToClimb,
            hasElevator: hasElevator.value,
            bringToTheDoor: bringToTheDoor.value,
            deliveryStartTime,
            deliveryEndTime,
        }

        const items = cartStore.items.map((item) => {
            const packagingId = item.packaging?.[0]?.id ?? null
            const quantityPack = is19lPackaging(item.packaging)
                ? cartStore.getDisplayQuantity(item.id)
                : 0
            return {
                productId: item.id,
                quantity: item.quantity,
                packagingId,
                quantityPack,
            }
        })

        // Distribute totalBottlesToReturn across 19L items sequentially,
        // reducing quantityPack (unreturned bottles) from the first item onwards.
        let remaining = cartStore.totalBottlesToReturn
        for (const item of items) {
            if (remaining <= 0) break
            if (item.quantityPack > 0) {
                const reduction = Math.min(item.quantityPack, remaining)
                item.quantityPack -= reduction
                remaining -= reduction
            }
        }

        const fees: OrderFee[] = computedFees.value

        // Build notes string
        const notesParts: string[] = []
        if (cartStore.bottlesFromOthers) {
            notesParts.push(
                `Клиентът връща ${cartStore.bottlesFromOthers} празни галона от друг доставчик`,
            )
        }
        if (deliveryComment.value) notesParts.push(deliveryComment.value)
        if (cityRules.value.showContactless && contactlessDelivery.value) {
            notesParts.push(t('order.contactlessDelivery'))
        }
        appendEarlierIfPossibleNote(notesParts)

        const basePayload: OrderPayload = {
            email: email.value,
            firstName: firstName.value,
            lastName: lastName.value,
            phone: phone.value,

            shippingAddress,

            ...invoicePayloadFields(phone.value, email.value),

            order: {
                payTypeId: paymentMethod.value.value,
                invoiceTypeId: invoiceTypeId.value,
                supplyPartyId: isCourierRequired.value ? 2 : 1,
                transportModeId: isCourierRequired.value ? 7 : 10,
                transportInformTypeId: transportInformTypeId.value,
                note: deliveryComment.value,
                deliveryStartTime,
                deliveryEndTime,
                items,
                fees,
            },
            notes: notesParts.join(' | '),
            createdByAdminId: null,
            deliveryDateText: deliveryDateTextForCustomer() || undefined,
            ...(paymentMethod.value.value === PaymentMethods.card.value
                ? { awaitCardPayment: true }
                : {}),
        }

        return basePayload
    }

    return {
        // address
        address,
        coordinates,
        city,
        savedAddressCity,
        neighborhood,
        street,
        block,
        entrance,
        floor,
        apt,
        elevatorChoice,
        hasElevator,
        bringToTheDoor,
        floor1IsGroundLevel,
        deliveryComment,
        additionalInfo,
        // contact
        firstName,
        lastName,
        email,
        phone,
        // delivery
        selectedDeliveryDay,
        tryEarlierIfPossible,
        callBeforeArrival,
        preCallTiming,
        transportInformTypeId,
        // options
        acceptedTerms,
        contactlessDelivery,
        // payment
        paymentMethod,
        // invoice
        wantInvoice,
        guestInvoice,
        eikNumber,
        eikLoading,
        eikError,
        identity,
        identityStatus,
        asRequest,
        linked,
        invoiceCompany,
        invoiceCompanyName,
        invoiceTypeId,
        invoiceTypeReady,
        orderHasInvoice,
        // promo
        // derived computeds
        cityRules,
        deliveryDayOptions,
        deliverToSofia,
        isCourierRequired,
        computedFees,
        // actions
        lookupEik,
        setIdentity,
        restoreGuestInvoice,
        loadIdentity,
        waitIdentity,
        loadOrderNoms,
        buildOrderPayload,
        setDefaultPaymentMethod,
        applyCityProfileSideEffects,
        setElevatorChoice,
        handlePersistRestore,
        clearPersonalData,
        applyAccountContact,
    }
}, {
    persist: {
        key: 'order-v1',
        pick: [
            'coordinates',
            'city',
            'neighborhood',
            'street',
            'block',
            'entrance',
            'floor',
            'apt',
            'elevatorChoice',
            'deliveryComment',
            'additionalInfo',
            'firstName',
            'lastName',
            'email',
            'phone',
            'callBeforeArrival',
            'preCallTiming',
            'contactlessDelivery',
            'paymentMethod',
            'guestInvoice',
        ],
        afterHydrate: (ctx) => {
            ctx.store.handlePersistRestore()
        },
    },
})

